import localUrls from '@/config/urls-local.json';
import productionUrls from '@/config/urls-production.json';
import stageUrls from '@/config/urls-stage.json';
import testUrls from '@/config/urls-test.json';

export type EnvironmentName = 'local' | 'test' | 'stage' | 'production';

interface UrlConfig {
  apiBaseUrl: string;
  i18nApiUrl: string;
}

const urlsByEnvironment: Record<EnvironmentName, UrlConfig> = {
  local: localUrls,
  test: testUrls,
  stage: stageUrls,
  production: productionUrls,
};

const defaultEnvironment: EnvironmentName = 'local';

const environmentAliases: Record<string, EnvironmentName> = {
  local: 'local',
  dev: 'local',
  development: 'local',
  test: 'test',
  testing: 'test',
  qa: 'test',
  sandbox: 'test',
  stage: 'stage',
  staging: 'stage',
  preprod: 'stage',
  uat: 'stage',
  prod: 'production',
  production: 'production',
};

export const normalizeEnvironmentName = (
  value: string | undefined,
): EnvironmentName => {
  const normalized = value?.trim().toLowerCase();

  if (!normalized) {
    return defaultEnvironment;
  }

  return environmentAliases[normalized] ?? defaultEnvironment;
};

export const getRuntimeEnvironmentName = (): EnvironmentName => {
  return normalizeEnvironmentName(window.environment?.name);
};

const runtimeEnvironment = getRuntimeEnvironmentName();
const selectedUrls = urlsByEnvironment[runtimeEnvironment];

const normalizeApiBaseUrl = (value: string | undefined): string => {
  const normalized = value?.trim().replace(/\/+$/, '') ?? '';

  if (!normalized) {
    return '';
  }

  // Endpoints already include /api/v1, so strip it from base URLs when provided.
  return normalized.replace(/\/api\/v1$/i, '');
};

const runtimeApiBaseUrl = normalizeApiBaseUrl(window.environment?.api?.baseUrl);
const envApiBaseUrl = normalizeApiBaseUrl(
  import.meta.env.VITE_ENTITY_PORTAL_API_BASE_URL,
);
const localFallbackApiBaseUrl =
  runtimeEnvironment === 'local'
    ? normalizeApiBaseUrl(localUrls.apiBaseUrl)
    : '';
const environmentConfigApiBaseUrl = normalizeApiBaseUrl(
  selectedUrls.apiBaseUrl,
);

export const urls: UrlConfig = {
  apiBaseUrl:
    runtimeApiBaseUrl ||
    envApiBaseUrl ||
    localFallbackApiBaseUrl ||
    environmentConfigApiBaseUrl,
  i18nApiUrl: import.meta.env.VITE_I18N_API_URL ?? selectedUrls.i18nApiUrl,
};
